import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { db, storage } from '../../firebase';
import { doc, updateDoc } from 'firebase/firestore';
import { ref, uploadBytesResumable, getDownloadURL, UploadTask } from 'firebase/storage';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';

const OnboardingBank: React.FC = () => {
  const navigate = useNavigate();
  const { firebaseUser, user, refreshUserData, logout } = useAuth();

  const [bankName, setBankName] = useState(user?.bankName || '');
  const [accountHolderName, setAccountHolderName] = useState(user?.accountHolderName || '');
  const [accountNumber, setAccountNumber] = useState(user?.accountNumber || '');
  const [ifscCode, setIfscCode] = useState(user?.ifscCode || '');
  const [upiId, setUpiId] = useState(user?.upiId || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [bankDoc, setBankDoc] = useState<File | null>(null);
  const [upiDoc, setUpiDoc] = useState<File | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStatus, setUploadStatus] = useState<string>('');
  const [bankProgress, setBankProgress] = useState(0);
  const [upiProgress, setUpiProgress] = useState(0);

  // File validation constants
  const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
  const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
  const UPLOAD_TIMEOUT_MS = 60000; // 60 seconds timeout
  const MAX_RETRIES = 2; // Maximum retry attempts

  // Validate and sanitize file
  const validateFile = (file: File, fileType: 'bank' | 'upi'): { valid: boolean; error?: string } => {
    // Check file type (MIME type)
    if (!file.type.startsWith('image/')) {
      return { valid: false, error: 'Please upload an image file (JPEG, PNG, or WebP)' };
    }

    // Check specific MIME types
    if (!ALLOWED_IMAGE_TYPES.includes(file.type.toLowerCase())) {
      return { valid: false, error: 'Only JPEG, PNG, and WebP images are allowed' };
    }

    // Check file extension
    const ext = file.name.split('.').pop()?.toLowerCase();
    const allowedExts = ['jpg', 'jpeg', 'png', 'webp'];
    if (!ext || !allowedExts.includes(ext)) {
      return { valid: false, error: 'File must have .jpg, .jpeg, .png, or .webp extension' };
    }

    // Check file size
    if (file.size > MAX_FILE_SIZE) {
      return { valid: false, error: `File size must be less than 5MB. Current size: ${(file.size / (1024 * 1024)).toFixed(2)}MB` };
    }

    return { valid: true };
  };

  // Sanitize file name
  const sanitizeFileName = (fileName: string): string => {
    // Remove special characters and keep only alphanumeric, dots, hyphens, underscores
    const sanitized = fileName.replace(/[^a-zA-Z0-9._-]/g, '_');
    // Limit length
    return sanitized.length > 100 ? sanitized.substring(0, 100) : sanitized;
  };

  // Get user-friendly error message
  const getErrorMessage = (error: any): string => {
    if (error.code === 'permission-denied') {
      return 'You do not have permission to perform this action. Please contact support.';
    }
    if (error.code === 'storage/quota-exceeded') {
      return 'Storage quota exceeded. Please contact support.';
    }
    if (error.code === 'storage/unauthorized') {
      return 'Upload permission denied. Please contact support.';
    }
    if (error.code === 'storage/canceled') {
      return 'Upload was canceled. Please try again.';
    }
    if (error.message?.includes('timeout') || error.message?.includes('network')) {
      return 'Network error. Please check your internet connection and try again.';
    }
    if (error.message?.includes('exceeds') || error.message?.includes('size')) {
      return 'File size too large. Please compress images before uploading.';
    }
    return error.message || 'An error occurred. Please try again.';
  };

  // Upload file with timeout, retry, and progress tracking
  const uploadFileWithProgress = async (
    file: File,
    path: string,
    fileLabel: string,
    progressSetter: (progress: number) => void,
    retryCount = 0
  ): Promise<string> => {
    return new Promise((resolve, reject) => {
      const uploadTask = uploadBytesResumable(ref(storage, path), file);
      
      // Set timeout
      const timeoutId = setTimeout(() => {
        uploadTask.cancel();
        reject(new Error(`Upload timeout: ${fileLabel} upload took too long. Please check your internet connection.`));
      }, UPLOAD_TIMEOUT_MS);

      // Track upload progress
      uploadTask.on(
        'state_changed',
        (snapshot) => {
          const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          progressSetter(progress);
        },
        (error) => {
          clearTimeout(timeoutId);
          // Retry on network errors
          if (retryCount < MAX_RETRIES && (
            error.code === 'storage/retry-limit-exceeded' ||
            error.message?.includes('network') ||
            error.message?.includes('timeout')
          )) {
            setTimeout(() => {
              uploadFileWithProgress(file, path, fileLabel, progressSetter, retryCount + 1)
                .then(resolve)
                .catch(reject);
            }, 1000 * (retryCount + 1)); // Exponential backoff
          } else {
            reject(error);
          }
        },
        async () => {
          clearTimeout(timeoutId);
          try {
            const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
            resolve(downloadURL);
          } catch (error) {
            reject(error);
          }
        }
      );
    });
  };

  const handleBankDocChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    if (!file) {
      setBankDoc(null);
      return;
    }

    const validation = validateFile(file, 'bank');
    if (!validation.valid) {
      setError(validation.error || 'Invalid file');
      e.target.value = ''; // Clear input
      setBankDoc(null);
      return;
    }

    setError(''); // Clear previous errors
    setBankDoc(file);
  };

  const handleUpiDocChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    if (!file) {
      setUpiDoc(null);
      return;
    }

    const validation = validateFile(file, 'upi');
    if (!validation.valid) {
      setError(validation.error || 'Invalid file');
      e.target.value = ''; // Clear input
      setUpiDoc(null);
      return;
    }

    setError(''); // Clear previous errors
    setUpiDoc(file);
  };

  // Load data from Firestore when user data is available
  useEffect(() => {
    if (user) {
      setBankName(prev => prev || user.bankName || '');
      setAccountHolderName(prev => prev || user.accountHolderName || '');
      setAccountNumber(prev => prev || user.accountNumber || '');
      setIfscCode(prev => prev || user.ifscCode || '');
      setUpiId(prev => prev || user.upiId || '');
    }
  }, [user]);

  // Update combined progress when individual progresses change
  useEffect(() => {
    if (saving && (bankDoc || upiDoc)) {
      const totalFiles = (bankDoc ? 1 : 0) + (upiDoc ? 1 : 0);
      if (totalFiles > 0) {
        const combinedProgress = ((bankDoc ? bankProgress : 0) + (upiDoc ? upiProgress : 0)) / totalFiles;
        setUploadProgress(combinedProgress);
        
        if (totalFiles === 2) {
          setUploadStatus(`Uploading documents... ${Math.round(combinedProgress)}%`);
        } else if (bankDoc) {
          setUploadStatus(`Uploading bank document... ${Math.round(bankProgress)}%`);
        } else if (upiDoc) {
          setUploadStatus(`Uploading UPI document... ${Math.round(upiProgress)}%`);
        }
      }
    }
  }, [bankProgress, upiProgress, bankDoc, upiDoc, saving]);

  // Validation function to check if form is valid
  const isFormValid = (): boolean => {
    // Check if user is blocked
    if (user?.kyc?.blockPermanent) return false;
    if (user?.kyc?.blockUntil && user.kyc.blockUntil > new Date()) return false;

    // Bank details REQUIRED
    const bank = bankName.trim();
    const holder = accountHolderName.trim();
    const acct = accountNumber.trim();
    const ifsc = ifscCode.trim();
    
    // Check if all required fields are filled
    if (!bank || !holder || !acct || !ifsc) return false;

    // Validate bank details format
    const bankOk = bank.length >= 2;
    const ifscOk = /^([A-Z]{4}0[0-9A-Z]{6})$/.test(ifsc.toUpperCase());
    const acctOk = acct.length >= 8;
    const holderOk = holder.length >= 2;
    
    if (!bankOk || !ifscOk || !acctOk || !holderOk) return false;

    // Bank document is required
    if (!bankDoc) return false;

    // UPI optional: if provided, validate format
    const upi = upiId.trim();
    if (upi) {
      const upiRegex = /^[a-zA-Z0-9._\-]{2,}@[a-zA-Z]{2,}$/;
      if (!upiRegex.test(upi)) return false;
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!firebaseUser) return;
    setError('');

    // Check if user is blocked
    if (user?.kyc?.blockPermanent) {
      setError('Your KYC submission is permanently blocked. Please contact support.');
      return;
    }
    if (user?.kyc?.blockUntil) {
      const blockDate = user.kyc.blockUntil;
      if (blockDate > new Date()) {
        const daysLeft = Math.ceil((blockDate.getTime() - Date.now()) / (1000 * 60 * 60 * 24));
        setError(`Your account is temporarily blocked. You can resubmit KYC after ${daysLeft} day${daysLeft > 1 ? 's' : ''}.`);
        return;
      }
    }

    // Bank details REQUIRED
    const bank = bankName.trim();
    const holder = accountHolderName.trim();
    const acct = accountNumber.trim();
    const ifsc = ifscCode.trim();
    
    if (!bank || !holder || !acct || !ifsc) {
      setError('All bank details are required');
      return;
    }

    // Validate bank details
    const bankOk = bank.length >= 2;
    const ifscOk = /^([A-Z]{4}0[0-9A-Z]{6})$/.test(ifsc.toUpperCase());
    const acctOk = acct.length >= 8;
    const holderOk = holder.length >= 2;
    
    if (!bankOk) {
      setError('Bank name must be at least 2 characters');
      return;
    }
    if (!ifscOk) {
      setError('Invalid IFSC Code format (e.g., SBIN0001234)');
      return;
    }
    if (!acctOk) {
      setError('Account number must be at least 8 digits');
      return;
    }
    if (!holderOk) {
      setError('Account holder name must be at least 2 characters');
      return;
    }

    // UPI optional: if provided, validate format
    const upi = upiId.trim();
    if (upi) {
      const upiRegex = /^[a-zA-Z0-9._\-]{2,}@[a-zA-Z]{2,}$/;
      if (!upiRegex.test(upi)) {
        setError('Invalid UPI ID format (e.g., name@bank)');
        return;
      }
    }

    setSaving(true);
    setError('');
    setUploadProgress(0);
    setUploadStatus('');
    
    try {
      const payload: Record<string, any> = {
        bankName: bank,
        accountHolderName: holder,
        accountNumber: acct,
        ifscCode: ifsc.toUpperCase(),
      };
      // Only save UPI if provided
      if (upi) {
        payload.upiId = upi;
      }
      // Require bank document
      if (!bankDoc) {
        setError('Please upload bank proof (passbook/cheque/statement)');
        setSaving(false);
        return;
      }
      const uid = firebaseUser.uid;
      
      // Reset progress states
      setBankProgress(0);
      setUpiProgress(0);
      setUploadProgress(0);
      
      // Prepare parallel uploads
      const uploadPromises: Promise<void>[] = [];
      
      // Upload bank document
      const bankExt = (bankDoc.name.split('.').pop() || 'jpg').toLowerCase();
      const bankPath = `users/${uid}/bank/onboarding-bank-proof-${Date.now()}.${bankExt}`;
      
      uploadPromises.push(
        uploadFileWithProgress(bankDoc, bankPath, 'Bank document', setBankProgress)
          .then((url) => {
            payload.bankDocUrl = url;
            setBankProgress(100);
          })
          .catch((uploadErr: any) => {
            setBankProgress(0);
            throw new Error(`Bank document upload failed: ${getErrorMessage(uploadErr)}`);
          })
      );
      
      // Upload UPI document if provided
      if (upi && upiDoc) {
        const upiExt = (upiDoc.name.split('.').pop() || 'jpg').toLowerCase();
        const upiPath = `users/${uid}/bank/onboarding-upi-proof-${Date.now()}.${upiExt}`;
        
        uploadPromises.push(
          uploadFileWithProgress(upiDoc, upiPath, 'UPI document', setUpiProgress)
            .then((url) => {
              payload.upiDocUrl = url;
              setUpiProgress(100);
            })
            .catch((uploadErr: any) => {
              setUpiProgress(0);
              throw new Error(`UPI document upload failed: ${getErrorMessage(uploadErr)}`);
            })
        );
      }
      
      // Execute all uploads in parallel
      if (uploadPromises.length > 0) {
        const totalFiles = uploadPromises.length;
        setUploadStatus(`Uploading ${totalFiles} document${totalFiles > 1 ? 's' : ''}...`);
        
        await Promise.all(uploadPromises);
        
        // Ensure both are at 100% after completion
        if (bankDoc) setBankProgress(100);
        if (upiDoc) setUpiProgress(100);
        setUploadProgress(100);
        setUploadStatus('Upload complete! Saving...');
      }

      // 1) Save bank details only (matches Firestore rules bank-only whitelist)
      setUploadStatus('Saving bank details...');
      await updateDoc(doc(db, 'users', firebaseUser.uid), payload);

      // 2) Clear needsBankResubmit flag in a separate update (matches KYC-only whitelist)
      await updateDoc(doc(db, 'users', firebaseUser.uid), {
        'kyc.needsBankResubmit': false,
      });

      await refreshUserData?.();
      setUploadStatus('Success! Redirecting...');
      navigate('/onboarding/pending', { replace: true });
    } catch (err: any) {
      setError(getErrorMessage(err));
    } finally {
      setSaving(false);
      setUploadProgress(0);
      setBankProgress(0);
      setUpiProgress(0);
      setUploadStatus('');
    }
  };

  const handleCancel = async () => {
    if (window.confirm('Are you sure you want to cancel registration? You will be logged out.')) {
      await logout();
      navigate('/', { replace: true });
    }
  };

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <div className="bg-[#ee4238] text-white px-6 pt-14 pb-8 rounded-b-[28px] text-center">
        <div className="flex items-center justify-between mb-4">
          <button
            onClick={handleCancel}
            className="text-white/90 hover:text-white transition-colors flex items-center gap-1 sm:gap-2 px-2 sm:px-0 py-1 sm:py-0"
          >
            <ArrowLeftIcon className="w-4 h-4 sm:w-5 sm:h-5" />
            <span className="text-xs sm:text-sm font-medium">Cancel</span>
          </button>
          <div className="flex-1"></div>
        </div>
        <h1 className="text-2xl font-black">Bank Details</h1>
        <p className="text-white/90 mt-2 text-sm">
          {user?.kyc?.needsBankResubmit 
            ? 'Please review and update your bank details for KYC resubmission.' 
            : 'Bank details are mandatory for withdrawals. UPI is optional.'}
        </p>
        
        {/* Blocking Status Messages */}
        {user?.kyc?.blockPermanent && (
          <div className="px-6 mt-4">
            <div className="rounded-2xl border border-red-300 bg-red-100 p-4">
              <div className="text-red-800 font-extrabold text-sm mb-1">🚫 PERMANENTLY BLOCKED</div>
              <div className="text-red-700/90 text-sm">
                Your KYC submission is permanently blocked. Please contact support for assistance.
              </div>
            </div>
          </div>
        )}

        {user?.kyc?.blockUntil && !user?.kyc?.blockPermanent && user.kyc.blockUntil > new Date() && (
          <div className="px-6 mt-4">
            <div className="rounded-2xl border border-red-300 bg-red-100 p-4">
              <div className="text-red-800 font-extrabold text-sm mb-1">⏰ TEMPORARILY BLOCKED</div>
              <div className="text-red-700/90 text-sm">
                You are rejected and temporarily blocked. Try again after {Math.ceil((user.kyc.blockUntil.getTime() - Date.now()) / (1000 * 60 * 60 * 24))} day{Math.ceil((user.kyc.blockUntil.getTime() - Date.now()) / (1000 * 60 * 60 * 24)) > 1 ? 's' : ''}.
              </div>
            </div>
          </div>
        )}
        
        {/* Progress Steps */}
        <div className="mt-6 flex justify-center items-center space-x-4">
          {/* Step 1 - Personal (Completed) */}
          <div className="flex flex-col items-center">
            <div className="w-8 h-8 bg-white rounded-full flex items-center justify-center">
              <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center">
                <span className="text-white text-xs font-bold">✓</span>
              </div>
            </div>
            <span className="text-white text-xs font-medium mt-1">Personal</span>
          </div>
          
          {/* Connector Line */}
          <div className="w-8 h-0.5 bg-white"></div>
          
          {/* Step 2 - KYC (Completed) */}
          <div className="flex flex-col items-center">
            <div className="w-8 h-8 bg-white rounded-full flex items-center justify-center">
              <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center">
                <span className="text-white text-xs font-bold">✓</span>
              </div>
            </div>
            <span className="text-white text-xs font-medium mt-1">KYC</span>
          </div>
          
          {/* Connector Line */}
          <div className="w-8 h-0.5 bg-white"></div>
          
          {/* Step 3 - Bank (Active) */}
          <div className="flex flex-col items-center">
            <div className="w-8 h-8 bg-white rounded-full flex items-center justify-center">
              <div className="w-6 h-6 bg-white rounded-full flex items-center justify-center">
                <span className="text-[#ee4238] text-xs font-bold">3</span>
              </div>
            </div>
            <span className="text-white text-xs font-medium mt-1">Bank</span>
          </div>
        </div>
        
        {/* Progress Bar */}
        <div className="mt-4 w-full bg-white/20 rounded-full h-1">
          <div className="bg-white h-1 rounded-full" style={{ width: '100%' }}></div>
        </div>
      </div>

      <form id="bankForm" onSubmit={handleSubmit} className="flex-1 p-6 space-y-4 pb-28">
        {error && <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded text-sm">{error}</div>}
        
        {/* Upload Progress Indicator */}
        {saving && uploadStatus && (
          <div className="p-4 bg-blue-50 border border-blue-200 rounded-2xl">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-semibold text-blue-800">{uploadStatus}</span>
              {uploadProgress > 0 && <span className="text-sm font-bold text-blue-600">{Math.round(uploadProgress)}%</span>}
            </div>
            {uploadProgress > 0 && (
              <div className="w-full bg-blue-200 rounded-full h-2">
                <div 
                  className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${uploadProgress}%` }}
                ></div>
              </div>
            )}
          </div>
        )}

        <div className="rounded-2xl border-2 border-[#ee4238] p-4 bg-red-50/30">
          <div className="flex items-baseline justify-between mb-2">
            <label className="block text-sm font-semibold">Bank Details</label>
            <span className="text-xs font-bold text-[#ee4238]">Required *</span>
          </div>
          <p className="text-xs text-gray-600 mb-3">All bank fields are mandatory for withdrawals</p>
        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium mb-2">Bank Name <span className="text-[#ee4238]">*</span></label>
            <input 
              value={bankName} 
              onChange={e => setBankName(e.target.value)} 
              placeholder="e.g., State Bank of India"
              className="w-full rounded-2xl border border-gray-300 bg-white px-4 py-3 outline-none focus:ring-2 focus:ring-[#ee4238]" 
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Account Holder Name <span className="text-[#ee4238]">*</span></label>
            <input 
              value={accountHolderName} 
              onChange={e => setAccountHolderName(e.target.value)} 
              placeholder="Full name as per bank"
              className="w-full rounded-2xl border border-gray-300 bg-white px-4 py-3 outline-none focus:ring-2 focus:ring-[#ee4238]" 
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Account Number <span className="text-[#ee4238]">*</span></label>
            <input 
              value={accountNumber} 
              onChange={e => setAccountNumber(e.target.value.replace(/\s/g,'').slice(0,18))} 
              inputMode="numeric" 
              placeholder="Enter account number"
              className="w-full rounded-2xl border border-gray-300 bg-white px-4 py-3 outline-none focus:ring-2 focus:ring-[#ee4238]" 
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">IFSC Code <span className="text-[#ee4238]">*</span></label>
            <input 
              value={ifscCode} 
              onChange={e => setIfscCode(e.target.value.toUpperCase().slice(0,11))} 
              placeholder="e.g., SBIN0001234"
              className="w-full rounded-2xl border border-gray-300 bg-white px-4 py-3 outline-none focus:ring-2 focus:ring-[#ee4238]" 
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Bank Proof (Passbook/Cheque/Statement) <span className="text-[#ee4238]">*</span></label>
            <input 
              type="file" 
              accept="image/jpeg,image/jpg,image/png,image/webp" 
              onChange={handleBankDocChange}
              className="w-full rounded-2xl border border-gray-300 px-4 py-3 outline-none focus:ring-2 focus:ring-[#ee4238]"
            />
            <p className="text-xs text-gray-500 mt-1">Upload a clear image showing account holder name, account number and IFSC. Max size: 5MB</p>
          </div>
        </div>
        </div>

        <div className="rounded-2xl border border-gray-200 p-4 bg-white">
          <div className="flex items-baseline justify-between mb-2">
            <label className="block text-sm font-semibold">UPI ID</label>
            <span className="text-xs text-gray-500">Optional</span>
          </div>
          <input 
            value={upiId} 
            onChange={e => setUpiId(e.target.value)} 
            placeholder="name@bank (optional)"
            className="w-full rounded-xl border border-gray-300 px-4 py-3 outline-none focus:ring-2 focus:ring-[#ee4238]" 
          />
          <div className="mt-3">
            <label className="block text-sm font-medium mb-2">UPI Proof (QR/Screenshot) <span className="text-gray-400">(optional)</span></label>
            <input 
              type="file" 
              accept="image/jpeg,image/jpg,image/png,image/webp" 
              onChange={handleUpiDocChange}
              className="w-full rounded-2xl border border-gray-300 px-4 py-3 outline-none focus:ring-2 focus:ring-[#ee4238]"
            />
            <p className="text-xs text-gray-500 mt-1">Upload your UPI QR or recent payment screenshot if adding UPI. Max size: 5MB</p>
          </div>
          <p className="text-xs text-gray-500 mt-2">Add UPI for faster instant payments (optional).</p>
        </div>

        <div className="h-4"></div>
      </form>

      {/* Fixed bottom action bar */}
      <div className="fixed inset-x-0 bottom-0 z-40 bg-white/95 backdrop-blur border-t border-gray-200 px-6 pt-3 pb-[max(16px,env(safe-area-inset-bottom))]">
        <div className="flex gap-3">
          <button
            type="button"
            onClick={() => navigate('/onboarding/kyc', { replace: false })}
            disabled={saving}
            className="flex-1 bg-gray-200 text-gray-700 py-4 px-6 font-extrabold rounded-2xl disabled:opacity-50 hover:bg-gray-300 transition-colors"
          >
            Previous
          </button>
          <button 
            type="submit" 
            form="bankForm" 
            disabled={
              saving || 
              !isFormValid()
            } 
            className="flex-1 bg-[#ee4238] text-white py-4 px-6 font-extrabold rounded-2xl disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving 
              ? (uploadStatus || 'Saving...')
              : (user?.kyc?.blockPermanent || (user?.kyc?.blockUntil && user.kyc.blockUntil > new Date()))
              ? 'Blocked'
              : (user?.kyc?.needsBankResubmit ? 'Resubmit Bank Details' : 'Submit Bank Details')
            }
          </button>
        </div>
      </div>
    </div>
  );
};

export default OnboardingBank;


